import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import PublicLayout from '@/components/layout/PublicLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { welcomeWeekService, type WelcomeWeekAdminContext } from '@/services/welcomeWeekService';
import { DEFAULT_WW_SEASON_CODE, type WelcomeWeekAdminCalendarSource } from '@/types/welcomeWeek';
import { buildSourceFullviewUrl, seasonQuery } from './wwUi';
import { ArrowLeft, CalendarDays, ExternalLink, Pencil, Settings } from 'lucide-react';
import { toast } from 'sonner';
import SourceEventEditor from './SourceEventEditor';

/**
 * Admin page for a central welcome-week *source* calendar. Mirrors the master
 * (Fadderprogram) admin: an editable CalOne full-week view plus an in-sync list
 * editor. Saving either regenerates the merged Omega master that groups merge
 * from. The label and source path are organization-specific.
 */
export default function TeknostartAdminPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const seasonCode = searchParams.get('season') || DEFAULT_WW_SEASON_CODE;
  const explicitSeason = searchParams.get('season');
  const [context, setContext] = useState<WelcomeWeekAdminContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState<WelcomeWeekAdminCalendarSource[]>([]);

  const canManage = useMemo(() => context?.role === 'owner' || context?.role === 'admin', [context?.role]);
  const centralSource = useMemo(() => sources.find((source) => source.source_key === 'central'), [sources]);
  const centralLabel = centralSource?.label || 'Central programme';

  const load = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const adminContext = await welcomeWeekService.getAdminContext(slug, seasonCode);
      setContext(adminContext);
      setSources(await welcomeWeekService.getMyCalendarSources(slug, seasonCode));
    } catch (error) {
      console.error('[CentralSourceAdmin] load failed', error);
      toast.error(error instanceof Error ? error.message : `Failed to load ${centralLabel} admin`);
    } finally {
      setLoading(false);
    }
  }, [seasonCode, slug]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PublicLayout>
      <div className="container py-8 space-y-6">
        <div>
          <Button asChild variant="ghost" className="px-0">
            <Link to={`/o/${slug}/ww${seasonQuery(explicitSeason)}`}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Welcome Week
            </Link>
          </Button>
        </div>
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{seasonCode}</Badge>
              {context?.role && <Badge variant="outline">{context.role}</Badge>}
            </div>
            <h1 className="mt-2 text-3xl font-bold tracking-tight">{centralLabel}</h1>
            <p className="text-muted-foreground">{context?.organization.name || slug}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to={`/o/${slug}/ww/admin${seasonQuery(explicitSeason)}`}>
                <Settings className="h-4 w-4 mr-2" />
                Master Admin
              </Link>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <CalendarDays className="h-10 w-10 animate-pulse text-muted-foreground" />
          </div>
        ) : !canManage ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">Owner or admin access is required.</p>
            </CardContent>
          </Card>
        ) : !centralSource ? (
          <Card>
            <CardContent className="py-10 text-center">
              <p className="text-muted-foreground">{centralLabel} editing is not available for this organization.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="overflow-hidden">
              <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                  <Pencil className="h-4 w-4" />
                  Edit week view ({centralLabel})
                  <Badge variant="secondary" className="ml-1">Editing</Badge>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <a href={buildSourceFullviewUrl(centralSource.calendar_username)} target="_blank" rel="noopener noreferrer">
                    Open in new window
                    <ExternalLink className="h-3.5 w-3.5 ml-1.5 opacity-70" />
                  </a>
                </Button>
              </div>
              <iframe
                title={`${centralLabel} editable full week view`}
                src={buildSourceFullviewUrl(centralSource.calendar_username)}
                className="w-full bg-background"
                style={{ height: 949, border: 0 }}
                loading="lazy"
              />
            </Card>

            <SourceEventEditor
              season={seasonCode}
              stem={centralSource.calendar_stem}
              label={centralSource.label}
            />
          </>
        )}
      </div>
    </PublicLayout>
  );
}
